import React, { useState, useCallback, useEffect } from 'react';
import Modal from '~/components/Modal';
import TemplateSelector from './TemplateSelector';
import TemplateEditor from './TemplateEditor';
import { Report, TemplateIndex } from '../Report.types';

type EditReportModalProps = {
    visible: boolean;
    onClose: () => void;
    onNewReport: (templateSelected: TemplateIndex, aReport?: Report) => void;
    onEditReport: (aReport: Report) => void;
    onDeleteReport: (aReport: Report) => void;
    isLoading: boolean;
    reportToEdit?: Report | null;
};

const ReportTemplateModal = ({
    visible,
    reportToEdit,
    onClose,
    onNewReport,
    onEditReport,
    onDeleteReport,
    isLoading,
}: EditReportModalProps) => {
    const [selectedTemplate, setSelectedTemplate] = useState<number | null>(
        reportToEdit == null // if the mode is new
            ? null // let the user select a template
            : // but if we're editing
              TemplateIndex.BLANK_TEMPLATE, // pre-select the custom template
    );

    const [previousVisible, setPreviousVisible] = useState<boolean>();

    useEffect(() => {
        // if the view is NOW becoming visible
        if (!previousVisible && visible) {
            if (
                reportToEdit == null && // and we're creating a new template
                selectedTemplate != null
            ) {
                // but a previous template was selected
                setSelectedTemplate(null); // make sure to deselect it so that the user starts from scratch
            } else if (reportToEdit != null) {
                // otherwise if we're editing
                setSelectedTemplate(TemplateIndex.BLANK_TEMPLATE); // skip the first screen
            }
        }
        setPreviousVisible(visible);
    }, [visible, reportToEdit, previousVisible, selectedTemplate]);

    const onReportTemplateSelected = useCallback(
        (selectedTemplate: TemplateIndex) => {
            if (selectedTemplate === TemplateIndex.BLANK_TEMPLATE) {
                setSelectedTemplate(selectedTemplate);
            } else {
                onNewReport(selectedTemplate);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    const backToTemplateSelection = useCallback(() => {
        setSelectedTemplate(null);
    }, []);

    return (
        <Modal isOpen={visible} onClose={onClose}>
            {selectedTemplate == null ? (
                <TemplateSelector
                    onReportTemplateSelected={onReportTemplateSelected}
                />
            ) : null}

            {selectedTemplate === TemplateIndex.BLANK_TEMPLATE ? (
                <TemplateEditor
                    reportToEdit={reportToEdit}
                    onGoBack={backToTemplateSelection}
                    onCancel={onClose}
                    onCreate={onNewReport}
                    isLoading={isLoading}
                    onDelete={onDeleteReport}
                    onEdit={onEditReport}
                />
            ) : null}
        </Modal>
    );
};

export default ReportTemplateModal;
